import { UseQueryOptions } from 'react-query';
import { CustomQueryOptions, FetchError, FetchResult } from 'src/types/TApiQueries';
import { addSentryApiBreadcrumb } from 'src/utils/sentry';
// import { isErrorResponseResult } from './errorHandlers';

type ResponseTransformer = 'json' | 'blob' | 'text';

const refetchInterval = 1 * 60 * 60 * 1000; // one minute

export async function getFetchResult<TData>(
  response: Response,
  transformer?: ResponseTransformer,
): Promise<FetchResult<TData>> {
  if (response.ok) {
    if (response.status === 202 || response.status === 204) return response as any;
    if (transformer === 'blob') return (await response.blob()) as any;
    if (transformer === 'text') return (await response.text()) as any;
    return await response.json();
  } else {
    const contentType = response.headers.get('content-type');
    let error = 'Unknown Error';
    if (contentType && contentType.includes('text/')) {
      error = await response.text();
    }
    if (contentType && contentType.includes('application/json')) {
      error = await response.json();
    }
    const message = `Request to "${response.url}" failed with ${response.status}`;
    return { message, error, status: response.status, url: response.url, statusText: response.statusText };
  }
}

export async function performFetchWithLogging<TData>(
  url: string,
  requestInit?: RequestInit,
  responseTransformer?: ResponseTransformer,
): Promise<FetchResult<TData>> {
  try {
    const response = await fetch(url, requestInit);
    const result = await getFetchResult<TData>(response, responseTransformer);
    return result;
  } catch (error) {
    throw error;
  }
}

/**
 * Retry a fetch helper that returns `FetchResult<T>` when it surfaces a
 * transient HTTP failure (429 or any 5xx) as an `ErrorResponseResult`.
 * Successful results — which don't carry a `.status` field — are returned
 * immediately, as are non-transient errors (401/403/404/etc). Uses
 * exponential backoff with light jitter so a burst of callers don't
 * synchronise their retries.
 *
 * Intended for endpoints called outside react-query (which has its own
 * retry policy). The first /usage paint fires ~17 requests concurrently;
 * the backend rate limiter occasionally 429s one or two of the /stats/*
 * batch, leaving stat cards stuck on the loading shimmer. Wrapping those
 * calls in this helper makes them self-heal.
 */
export async function retryOnTransientStatus<T>(
  fn: () => Promise<T>,
  options: { maxRetries?: number; baseDelayMs?: number } = {},
): Promise<T> {
  const { maxRetries = 2, baseDelayMs = 250 } = options;
  let result!: T;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (attempt > 0) {
      const jitter = 0.5 + Math.random();
      const delay = baseDelayMs * Math.pow(2, attempt - 1) * jitter;
      await new Promise((r) => setTimeout(r, delay));
    }
    result = await fn();
    const status = (result as unknown as { status?: number } | null | undefined)?.status;
    const isTransient =
      status === 429 || (typeof status === 'number' && status >= 500 && status < 600);
    if (!isTransient) return result;
  }
  return result;
}

/**
 * react-query `retry` predicate that retries only transient HTTP failures
 * (429 or any 5xx). The /usage Overview page fans out ~20-25 requests on
 * first paint; the backend rate limiter occasionally 429s one, which would
 * otherwise leave count cards (Shares/Collections) stuck on the shimmer.
 * Does NOT retry 401/403/404 (authoritative) so we don't loop on those.
 */
export function retryOnTransient(failureCount: number, error: FetchError): boolean {
  if (failureCount >= 2) return false;
  const status = (error as { status?: number } | null)?.status;
  if (typeof status !== 'number') return false;
  return status === 429 || (status >= 500 && status < 600);
}

export function getQueryString({ url, query = {} }: { url: string; query?: {} }): string {
  let composedUrl = new URL(url);
  let params = Object.entries(query)
    .filter(([k, v]) => v !== null && v !== '' && typeof v !== 'undefined')
    .map(([k, v]) => [String(k), String(v)]);
  composedUrl.search = new URLSearchParams(params).toString();

  return composedUrl.toString();
}

export function getStandardHeaders(accessToken?: string) {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-FileSpin-Client': 'filespin-ui',
  };

  if (accessToken) {
    return {
      ...headers,
      Authorization: accessToken,
    };
  }

  return headers;
}

export function getUploadHeaders(accessToken?: string) {
  const headers: HeadersInit = {
    Accept: 'application/json',
    'X-FileSpin-Client': 'filespin-ui',
  };

  if (accessToken) {
    return {
      ...headers,
      Authorization: accessToken,
    };
  }

  return headers;
}

export function mergeQueryOptions<T>(customOptions?: CustomQueryOptions, options?: UseQueryOptions<T, FetchError>) {
  const queryOptions = {
    ...options,
    ...customOptions,
  };

  if (customOptions?.shouldRefetch) {
    queryOptions.refetchInterval = refetchInterval;
  }

  return queryOptions;
}
